<template>
  <PTransparentLoader :loading="loading">
    <div v-if="movementOrderItem">
      <!-- Back button and Header -->
      <div class="d-flex align-items-center mb-3">
        <CLink
          style="color: #100F23"
          class="mr-4"
          @click="goBack"
        >
          <CIcon size="custom-size" :width="18" name="cipArrowNarrowLeft" class="mr-2"/>
          <translate translate-context="accounting.order">Back</translate>
        </CLink>
        <h1 class="main-header mb-0">
          <translate translate-context="accounting.orderDetail">Order</translate>
          {{ orderNumber }}
        </h1>
      </div>

      <!-- Switcher tra order item fratelli -->
      <div v-if="movementSiblingItems.length > 1" class="item-switcher mb-3">
        <CButton
          v-for="sibling in movementSiblingItems"
          :key="sibling.id"
          size="sm"
          :color="sibling.id === movementOrderItem.id ? 'primary' : 'secondary'"
          :variant="sibling.id === movementOrderItem.id ? '' : 'outline'"
          class="mr-2 mb-1"
          @click="switchToItem(sibling.id)"
        >
          <CIcon name="cil-camera" size="sm" class="mr-1"/>
          {{ sibling.pica_service_name || `#${sibling.id}` }}
        </CButton>
      </div>

      <!-- Order information -->
      <CCard class="mb-3">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipShoppingBag01" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="accounting.movement">Order information</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="4" v-if="hasScopePerm('accounting.movements.fields.event_name')">
              <div class="info-cell">
                <div class="info-label" v-translate translate-context="accounting.movement">Event</div>
                <div class="info-value">{{ movementOrderItem.pica_service_name || '-' }}</div>
              </div>
            </CCol>
            <CCol md="4" v-if="hasScopePerm('accounting.movements.fields.order_number')">
              <div class="info-cell">
                <div class="info-label" v-translate translate-context="accounting.movement">Order</div>
                <div class="info-value">{{ movementOrderItem.order_number || '-' }}</div>
              </div>
            </CCol>
            <CCol md="4">
              <div class="info-cell">
                <div class="info-label" v-translate translate-context="accounting.movement">Payment</div>
                <div class="info-value">
                  <template v-if="movementOrderItem.payment_method">
                    {{ movementOrderItem.payment_method.label || movementOrderItem.payment_method }}
                  </template>
                  <span v-else>-</span>
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Media -->
      <MovementOrderItemCard
        :item="movementOrderItem"
        :currency="currency"
      />

      <!-- Sezione 3: Rimborso -->
      <div class="order-section order-section--no-border" v-if="hasScopePerm('accounting.order_item.refund')">
        <RefundBox
          :order-item="movementOrderItem"
          :scope="scope"
          :removed-photos="removedPhotos"
          :purchased-count="purchasedCount"
          store-namespace="accounting/movement"
          @clear-photos="clearPhotoSelections"
          @select-all-removed="selectAllPurchasedAsRemoved"
        />
      </div>

      <!-- Refunds -->
      <CCard v-if="hasRefunds" class="mb-3">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipReload" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="accounting.movement">Refunds</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <table class="table table-striped mb-0">
            <thead>
              <tr>
                <th v-translate translate-context="accounting.movement"></th>
                <th v-translate translate-context="accounting.movement">Date</th>
                <th v-translate translate-context="accounting.movement">Refund type</th>
                <th class="text-right" v-translate translate-context="accounting.movement">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="refund in movementOrderItem.refunds" :key="refund.id">
                <td>
                  <router-link :to="getRefundRoute(refund.id)" class="reference-link">
                    #{{ refund.id }}
                  </router-link>
                </td>
                 <td>{{ refund.created_at|formatDateTime('L LT') }}</td>
                <td>
                  <CBadge v-if="refund.refund_type" color="primary" class="badge-outline">
                    {{ refund.refund_type.name }}
                  </CBadge>
                  <span v-else class="text-muted">-</span>
                </td>

                <td class="text-right text-danger">-{{ refund.amount|currency(currency) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-weight-medium">
                <td colspan="3" v-translate translate-context="accounting.movement">Total refunded</td>
                <td class="text-right text-danger">-{{ movementOrderItem.refund_summary.total_refunded|currency(currency) }}</td>
              </tr>
            </tfoot>
          </table>
        </CCardBody>
      </CCard>

      <CCard v-if="hasRevenueBreakdown">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipStats" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="accounting.movement">Revenue breakdown</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <!-- Waterfall: Gross → VAT → Net -->
          <div class="order-row" v-if="hasScopePerm('accounting.movements.fields.gross')">
            <div class="label" v-translate translate-context="accounting.movement">Gross revenue</div>
            <div class="value">{{ movementOrderItem.gross|currency(currency) }}</div>
          </div>
          <div class="order-row" v-if="hasScopePerm('accounting.movements.fields.vat')">
            <div class="label" v-translate translate-context="accounting.movement">VAT</div>
            <div class="value">-{{ movementOrderItem.vat|currency(currency) }}</div>
          </div>
          <div class="order-row order-row--border font-weight-semi-bold" v-if="hasScopePerm('accounting.movements.fields.net_revenue')">
            <div class="label" v-translate translate-context="accounting.movement">Net revenue</div>
            <div class="value">{{ movementOrderItem.net|currency(currency) }}</div>
          </div>
          <div class="order-row" v-if="hasTransactionCost">
            <div class="label" v-translate translate-context="accounting.movement">Transaction cost</div>
            <div class="value">-{{ movementOrderItem.transaction_cost|currency(currency) }}</div>
          </div>
          <div class="order-row order-row--border font-weight-semi-bold" v-if="hasTransactionCost && hasStakeholderSummary">
            <div class="label" v-translate translate-context="accounting.movement">Total payout</div>
            <div class="value">{{ stakeholderTotal|currency(currency) }}</div>
          </div>

          <!-- Stakeholder split -->
          <CRow v-if="hasStakeholderSummary" class="mt-3">
            <CCol
              v-for="(item, index) in stakeholderSummaryRows"
              :key="index"
              lg="6"
            >
              <div class="payout-cell payout-cell--border">
                <div class="label">{{ item.label }}</div>
                <div class="d-flex align-items-center justify-content-between">
                  <div class="value">{{ item.total|currency(currency) }}</div>
                  <!--<div class="ml-1">{{ item.percentage }}%</div>-->
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

    </div>
  </PTransparentLoader>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import MovementOrderItemCard from './components/MovementOrderItemCard.vue'
import RefundBox from '../order/components/RefundBox.vue'
import {
  getMovementRoutePrefix,
  getMovementDetailRouteParams,
  MOVEMENT_TYPES,
  STAKEHOLDER_TYPE_LABELS
} from '../constants'
import ConfigScope from "@/domain/core/constant/configScope";

export default {
  name: 'MovementOrderItemDetail',
  components: {
    PTransparentLoader,
    MovementOrderItemCard,
    RefundBox
  },
  data() {
    return {
      loading: true,
      removedPhotos: []
    }
  },
  async mounted() {
    await this.setScope(this.scope)
    await this.fetchScopedConfig()
    await this.loadItem()
  },
  computed: {
    ...mapGetters('accounting/movement', ['movementOrderItem', 'movementSiblingItems', 'movementStakeholderSummary', 'getMovementOrderItemMedia']),

    scopeType() {
      return this.$route.meta.scopeType || 'pica-service'
    },

    scopeId() {
      if (this.scopeType === 'global') {
        return null
      } else if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      return getMovementRoutePrefix(this.scopeType)
    },

    orderNumber() {
      return this.movementOrderItem?.order_number || `#${this.movementOrderItem?.id}`
    },

    currency() {
      return this.movementOrderItem?.currency || 'EUR'
    },

    hasOrderInfo() {
      return this.movementOrderItem?.order_number || this.movementOrderItem?.payment_method
    },

    purchasedCount() {
      if (!this.movementOrderItem) return 0
      return this.movementOrderItem.purchased_count || 0
    },

    canManagePhotos() {
      return this.movementOrderItem && this.movementOrderItem.refunds !== null && this.hasScopePerm('accounting.refund.media.remove')
    },

    removedPhotoIds() {
      return this.removedPhotos.map(p => p.id)
    },

    hasRefunds() {
      return this.movementOrderItem?.refunds?.length > 0
    },

    hasStakeholderSummary() {
      return this.movementStakeholderSummary && this.movementStakeholderSummary.length > 0
    },

    hasRevenueBreakdown() {
      return this.movementOrderItem && this.movementOrderItem.gross != null && this.hasStakeholderSummary
    },

    stakeholderPermissionMap() {
      return {
        'PICA': 'accounting.movements.fields.net_pica',
        'PHOTO_SERVICE': 'accounting.movements.fields.net_photo_service',
      }
    },

    stakeholderSummaryRows() {
      if (!this.hasStakeholderSummary) return []
      const filtered = this.movementStakeholderSummary.filter(s => {
        const typeKey = (s.stakeholder_type || '').toUpperCase()
        const perm = this.stakeholderPermissionMap[typeKey]
        return !perm || this.hasScopePerm(perm)
      })
      const stakeholderTotal = filtered.reduce(
        (sum, s) => sum + (Math.abs(parseFloat(s.total)) || 0), 0
      )
      return filtered.map(s => {
        const typeKey = (s.stakeholder_type || '').toUpperCase()
        const total = parseFloat(s.total) || 0
        return {
          label: STAKEHOLDER_TYPE_LABELS[typeKey] || s.stakeholder_type,
          total: s.total,
          percentage: stakeholderTotal > 0 ? ((Math.abs(total) / stakeholderTotal) * 100).toFixed(1) : '0.0'
        }
      })
    },

    hasTransactionCost() {
      return this.movementOrderItem && parseFloat(this.movementOrderItem.transaction_cost) > 0
    },

    stakeholderTotal() {
      return this.stakeholderSummaryRows.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0)
    }
  },
  watch: {
    '$route.params.id'() {
      this.loadItem()
    }
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/movement', ['fetchMovementOrderItem']),
    ...mapActions('login', ['fetchConfig']),

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchScopedConfig() {
      try {
        let configType, configId, configScope = null
        if (this.scopeType === 'pica-service') {
          configType = 'accounting_pica_service'
          configId = this.scopeId
          configScope = ConfigScope.PICA_SERVICE
        } else if (this.scopeType === 'contract-manager') {
          configType = 'accounting_contract_manager'
          configId = this.scopeId
        } else {
          return
        }
        await this.fetchConfig({type: configType, id: configId, scope: configScope})
      } catch (e) {
        // Config endpoint may not exist yet
      }
    },

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({ name: this.listRouteName, params })
    },

    async loadItem() {
      this.loading = true
      try {
        await this.fetchMovementOrderItem(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    switchToItem(itemId) {
      if (itemId === this.movementOrderItem?.id) return
      const route = {
        name: this.$route.name,
        params: {...this.$route.params, id: itemId}
      }
      this.$router.push(route)
    },

    getRefundRoute(refundId) {
      return getMovementDetailRouteParams(MOVEMENT_TYPES.REFUND, refundId, this.scope)
    },

    onRemovePhotos(photos) {
      for (const photo of photos) {
        if (!this.removedPhotos.find(p => p.id === photo.id)) {
          this.removedPhotos.push(photo)
        }
      }
    },

    clearPhotoSelections() {
      this.removedPhotos = []
    },

    onClearStatusPhotos(photoIds) {
      this.removedPhotos = this.removedPhotos.filter(p => !photoIds.includes(p.id))
    },

    selectAllPurchasedAsRemoved() {
      if (!this.movementOrderItem) return
      const mediaData = this.getMovementOrderItemMedia(this.movementOrderItem.id)
      if (!mediaData.media.length) return
      this.removedPhotos = [...mediaData.media]
    },
  }
}
</script>

<style scoped>
.order-section {
  padding: 20px 0;
  border-bottom: 1px solid #e4e7ea;
}

.order-section:last-of-type {
  border-bottom: none;
}

.order-section--no-border {
  border-bottom: none;
  padding-bottom: 0;
}

.order-section__header {
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  color: #333;
}

.item-switcher {
  display: flex;
  flex-wrap: wrap;
}


.reference-link {
  color: #e67e22;
  text-decoration: underline;
  font-weight: 500;
}

.reference-link:hover {
  color: #d35400;
}


.breakdown-blocks {
  display: flex;
  gap: 1rem;
}

.breakdown-block {
  flex: 1;
}

.breakdown-block__label {
  font-size: 0.85rem;
  color: #636f83;
  margin-bottom: 0.25rem;
}

.breakdown-block__amount {
  font-weight: 600;
}

.breakdown-total-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff3e0;
  border-radius: 6px;
  padding: 0.6rem 1rem;
  color: #e65100;
}
</style>
